const MIN_LENGTH = 2;
const MAX_LENGTH = 30;
const validPattern = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
const purelyNumeric = /^[0-9]+$/;
const consecutiveHyphens = /--/;
// A Mongo ObjectId is exactly 24 hex characters, which is otherwise a legal
// username. Forbidding this shape keeps usernames and ObjectIds disjoint, so
// the /user/:idOrUsername route can classify its parameter unambiguously.
const objectIdShaped = /^[0-9a-f]{24}$/;

/**
 * Check if a string is a valid username.
 * Valid usernames are lowercase alphanumeric with hyphens, 2-30 chars,
 * no leading/trailing/consecutive hyphens, not purely numeric, and not
 * shaped like a Mongo ObjectId (24 hex chars).
 * @param {string} str
 * @returns {boolean}
 */
export function isValidUsername(str) {
  if (typeof str !== "string") return false;
  if (str.length < MIN_LENGTH || str.length > MAX_LENGTH) return false;
  if (purelyNumeric.test(str)) return false;
  if (consecutiveHyphens.test(str)) return false;
  if (objectIdShaped.test(str)) return false;
  return validPattern.test(str);
}

// Latin letters that Unicode doesn't decompose into a base letter plus a
// combining mark, so stripping marks alone would drop them.
/** @type {Record<string, string>} */
const undecomposableLetters = {
  ł: "l",
  ø: "o",
  đ: "d",
  ð: "d",
  ß: "ss",
  æ: "ae",
  œ: "oe",
  ı: "i",
  þ: "th",
};

/**
 * Convert a display name into a URL-safe username slug.
 * Lowercases, transliterates accented Latin letters to plain ASCII (é → e,
 * ł → l, ß → ss), replaces word separators with hyphens, strips invalid chars,
 * collapses consecutive hyphens, trims leading/trailing hyphens.
 * Underscores, periods and plusses count as separators so email local-parts
 * like "ahmed.b1990" slug to "ahmed-b1990"; display names are unaffected
 * because sanitizeName only leaves periods at the end of a word, where they
 * already collapsed away.
 * @param {string} str
 * @returns {string}
 */
export function slugifyUsername(str) {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[łøđðßæœıþ]/g, (letter) => undecomposableLetters[letter])
    .replace(/[\s._+]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Derive a valid username from a display name, falling back to a caller
 * supplied name (the email local-part on signup) when the display name has no
 * slug-able characters — as happens for non-Latin names — and finally to a
 * generic base. Guarantees the output satisfies isValidUsername by padding,
 * prefixing, and truncating as needed.
 * @param {string} displayName
 * @param {string} [fallbackName]
 * @returns {string}
 */
export function deriveUsername(displayName, fallbackName) {
  let slug =
    slugifyUsername(displayName || "") || slugifyUsername(fallbackName || "");
  if (!slug) slug = "user";
  // Truncate, preferring a hyphen boundary to avoid breaking words
  if (slug.length > MAX_LENGTH) {
    slug = slug.slice(0, MAX_LENGTH);
    const lastHyphen = slug.lastIndexOf("-");
    if (lastHyphen > 0) slug = slug.slice(0, lastHyphen);
    slug = slug.replace(/-+$/, "");
  }
  // The remaining rules run AFTER truncation: cutting at a hyphen boundary
  // can leave a purely numeric or too-short slug (e.g. truncating
  // "0110101011101-10011101110111010101" back to its first hyphen leaves
  // "0110101011101").
  // Prepend "u" for purely numeric slugs (IDs often look like this)
  if (purelyNumeric.test(slug)) {
    slug = ("u" + slug).slice(0, MAX_LENGTH);
  }
  // Pad short slugs so they meet the minimum length
  if (slug.length < MIN_LENGTH) slug = (slug + "0").slice(0, MIN_LENGTH);
  // Never emit an ObjectId-shaped slug (see objectIdShaped): prefixing "u"
  // keeps it valid while making it 25 chars, so it can't be mistaken for an id.
  if (objectIdShaped.test(slug)) slug = ("u" + slug).slice(0, MAX_LENGTH);
  return slug;
}

/**
 * Build the canonical path for a user's profile, preferring their username but
 * falling back to their id when the username isn't a valid slug — e.g. a user
 * the display-name migration hasn't processed yet (raw usernames may contain
 * spaces/uppercase). Keeps profile links working during the deploy→migration
 * window and for any un-migrated straggler. The /user/:idOrUsername route
 * resolves both forms.
 * @param {string | undefined} username
 * @param {string} id
 * @returns {string}
 */
export function userProfilePath(username, id) {
  return "/user/" + (isValidUsername(username) ? username : id);
}

/**
 * Append a numeric suffix to a base username, truncating the base if needed
 * so the result fits within MAX_LENGTH. Pass the suffix as a string to
 * preserve zero-padding (e.g. "0042").
 * Guarantees a valid username given a valid base: an all-hex base plus digits
 * can land on exactly 24 hex chars (ObjectId shape), and truncating a
 * digit-leading base can leave a purely numeric result — prefixing "u"
 * escapes both without disturbing the suffix.
 * @param {string} base
 * @param {number | string} suffix
 * @returns {string}
 */
export function appendSuffix(base, suffix) {
  const suffixStr = String(suffix);
  const truncated = base.slice(0, MAX_LENGTH - suffixStr.length);
  const candidate = truncated + suffixStr;
  if (purelyNumeric.test(candidate) || objectIdShaped.test(candidate)) {
    return ("u" + base).slice(0, MAX_LENGTH - suffixStr.length) + suffixStr;
  }
  return candidate;
}
